import React, { useState, useRef, useEffect, useContext } from 'react';
import { useAppSelector } from '../../../../../../stores/hooks';
import { isFacilitator } from '../../../../../../functions/FilterUsers';
import { RootState } from '../../../../../../stores/store';
import styles from './Timer.module.scss';
import { WebsocketContext } from '../../../../../../context/WebsocketContext';

const pad = (num: string | number) => num.toString().padStart(2, '0');

const Confetti: React.FC = () => (
  <svg className={styles.confetti} viewBox="0 0 100 60">
    <circle cx="10" cy="50" r="2" fill="#FFD700">
      <animate attributeName="cy" from="50" to="10" dur="1s" repeatCount="indefinite" />
    </circle>
    <circle cx="30" cy="55" r="2" fill="#FF69B4">
      <animate attributeName="cy" from="55" to="15" dur="1.2s" repeatCount="indefinite" />
    </circle>
    <circle cx="50" cy="52" r="2" fill="#00BFFF">
      <animate attributeName="cy" from="52" to="12" dur="1.1s" repeatCount="indefinite" />
    </circle>
    <circle cx="70" cy="54" r="2" fill="#32CD32">
      <animate attributeName="cy" from="54" to="14" dur="1.3s" repeatCount="indefinite" />
    </circle>
    <circle cx="90" cy="53" r="2" fill="#FF8C00">
      <animate attributeName="cy" from="53" to="13" dur="1.15s" repeatCount="indefinite" />
    </circle>
  </svg>
);

interface TimerProps {
  onFinish?: () => void;
  isEditable?: boolean;
}

// Função para criar o som de finalização do timer
function playDingDong() {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const ding = ctx.createOscillator();
  const gainDing = ctx.createGain();
  ding.type = 'sine';
  ding.frequency.value = 1046;
  gainDing.gain.value = 0.18;
  ding.connect(gainDing);
  gainDing.connect(ctx.destination);
  ding.start();
  gainDing.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
  ding.stop(ctx.currentTime + 0.22);

  const dong = ctx.createOscillator();
  const gainDong = ctx.createGain();
  dong.type = 'sine';
  dong.frequency.value = 523;
  gainDong.gain.value = 0.16;
  dong.connect(gainDong);
  gainDong.connect(ctx.destination);
  dong.start(ctx.currentTime + 0.18);
  gainDong.gain.setValueAtTime(0.16, ctx.currentTime + 0.18);
  gainDong.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
  dong.stop(ctx.currentTime + 0.55);

  dong.onended = () => ctx.close();
}

const Timer: React.FC<TimerProps> = ({ onFinish, isEditable = true }) => {
  const [editing, setEditing] = useState<'minutes' | 'seconds' | null>(null);
  const [minutes, setMinutes] = useState('03');
  const [seconds, setSeconds] = useState('00');
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const minRef = useRef<HTMLInputElement>(null);
  const secRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef(minutes);
  const secondsRef = useRef(seconds);
  const [isFinished, setIsFinished] = useState(false);
  const { send, lastJsonMessage } = useContext(WebsocketContext);

  const sessionStore = useAppSelector((state: RootState) => state.session);
  const canvasStore = useAppSelector((state: RootState) => state.canvas);
  const isFacilitatorUser = canvasStore.feedback && isFacilitator(canvasStore.feedback.users, sessionStore.email);

  useEffect(() => { minutesRef.current = minutes; }, [minutes]);
  useEffect(() => { secondsRef.current = seconds; }, [seconds]);

  // Função auxiliar para criar payload do timer
  const createTimerPayload = (minutes: number, seconds: number, isRunning: boolean, additionalData: Record<string, any> = {}) => {
    return ({
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      isRunning: isRunning,
      ...additionalData
    });
  };

  // Função auxiliar para salvar timer no localStorage
  const saveTimerToStorage = (minutes: number, seconds: number, isRunning: boolean, additionalData: Record<string, any> = {}) => {

    localStorage.setItem("timer", JSON.stringify({
      minutes,
      seconds,
      isRunning,
      ...additionalData
    }));
  };

  // Função auxiliar para enviar evento via websocket
  const sendTimerEvent = (event: string, minutes: number, seconds: number, isRunning: boolean, additionalData: Record<string, any> = {}) => {
    send(event, createTimerPayload(minutes, seconds, isRunning, additionalData));
  };

  // Função auxiliar para limpar timer
  const sendClearTimer = () => {
    sendTimerEvent("timer", 0, 0, false, { isCleared: true });
  };

  // Função para deixar o timer em 00:00
  const resetTimerLocal = () => {
    localStorage.removeItem("timer");
    setMinutes('00');
    setSeconds('00');
  };

  // Função auxiliar para finalizar timer
  const finishTimer = () => {
    setIsRunning(false);
    resetTimerLocal();
    setIsFinished(true);
    setTimeout(() => playDingDong(), 50);
    if (onFinish) onFinish();
    setTimeout(() => setIsFinished(false), 2000);
    sendClearTimer();
  };

  useEffect(() => {
    if (!lastJsonMessage) return
    if (lastJsonMessage.action !== "timer") return

    const timerData = lastJsonMessage.data

    if (timerData["isCleared"] === true) {
      localStorage.removeItem("timer");
      resetTimerLocal();
      return;
    } else {

      if (timerData.isRunning) {
        setIsRunning(true);
        setMinutes(pad(timerData.minutes));
        setSeconds(pad(timerData.seconds));
        saveTimerToStorage(timerData.minutes, timerData.seconds, timerData.isRunning, { startedAt: new Date().toISOString() });
      } else {
        setIsRunning(false);
        setMinutes(pad(timerData.minutes));
        setSeconds(pad(timerData.seconds));
        saveTimerToStorage(timerData.minutes, timerData.seconds, timerData.isRunning, { pausedAt: new Date().toISOString() });
      }
    }

  }, [lastJsonMessage])

  // Carregar estado do timer do localStorage ao montar o componente
  useEffect(() => {
    const savedTimer = localStorage.getItem("timer");

    if (!savedTimer) return

    try {
      const timerData = JSON.parse(savedTimer);

      if (timerData.isRunning && timerData.startedAt) {
        // Timer estava rodando, calcular tempo decorrido
        const startTime = new Date(timerData.startedAt);
        const currentTime = new Date();
        const elapsedMs = currentTime.getTime() - startTime.getTime();
        const elapsedSeconds = Math.floor(elapsedMs / 1000);

        const totalInitialSeconds = (parseInt(timerData.minutes) * 60) + parseInt(timerData.seconds);
        const remainingSeconds = Math.max(0, totalInitialSeconds - elapsedSeconds);

        const remainingMinutes = Math.floor(remainingSeconds / 60);
        const remainingSecs = remainingSeconds % 60;

        if (remainingSeconds > 0) {
          // Ainda há tempo restante, continuar o timer
          setMinutes(pad(remainingMinutes));
          setSeconds(pad(remainingSecs));
          setIsRunning(true);
        } else {
          // Tempo esgotou, limpar localStorage
          localStorage.removeItem("timer");
          setMinutes('00');
          setSeconds('00');
          setIsFinished(true);
          setTimeout(() => setIsFinished(false), 2000);
        }
      } else if (!timerData.isRunning && timerData.pausedAt) {
        // Timer estava pausado, restaurar tempo
        setMinutes(pad(timerData.minutes));
        setSeconds(pad(timerData.seconds));
        setIsRunning(false);
      }
    } catch (error) {
      console.error("Erro ao carregar timer do localStorage:", error);
      localStorage.removeItem("timer");
    }
  }, []);

  useEffect(() => {
    if (editing === 'minutes' && minRef.current) {
      minRef.current.focus();
      minRef.current.select();
    } else if (editing === 'seconds' && secRef.current) {
      secRef.current.focus();
      secRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        let min = parseInt(minutesRef.current, 10);
        let sec = parseInt(secondsRef.current, 10);
        if (isNaN(min)) min = 0;
        if (isNaN(sec)) sec = 0;
        if (min === 0 && sec === 0) {
          finishTimer();
          return;
        }
        if (sec === 0) {
          if (min > 0) {
            setMinutes(pad(min - 1));
            setSeconds(pad(59));
          } else {
            setSeconds(pad(0));
          }
        } else {
          setSeconds(pad(sec - 1));
        }
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning]);

  const handleDisplayClick = (part: 'minutes' | 'seconds') => {
    if (isRunning) return;
    setEditing(part);
  };

  const handleBlur = () => {
    if (editing === 'minutes') setMinutes((m) => pad(validateValue(m)));
    if (editing === 'seconds') setSeconds((s) => pad(validateValue(s)));
    setEditing(null);
  };

  const validateValue = (value: string) => {
    let num = parseInt(value, 10);
    if (isNaN(num)) num = 0;
    if (num > 59) num = 59;
    return num.toString();
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 2) value = value.slice(0, 2);
    if (parseInt(value, 10) > 59) value = '59';
    setter(value);
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTimeChange(e, setMinutes);
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleTimeChange(e, setSeconds);
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      setEditing('seconds');
    }
  };

  const handleSecondsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      setEditing('minutes');
    }
  };

  const handlePlayPause = () => {
    if (isFinished) {
      setIsFinished(false);
      sendClearTimer();
      resetTimerLocal();
    } else {
      const currentMinutes = parseInt(minutes, 10);
      const currentSeconds = parseInt(seconds, 10);

      if (isRunning) {
        // Pausar timer
        setIsRunning(false);
        sendTimerEvent("timer", currentMinutes, currentSeconds, false, {
          pausedAt: new Date().toISOString()
        });
        saveTimerToStorage(currentMinutes, currentSeconds, false, {
          pausedAt: new Date().toISOString()
        });
      } else {
        // Iniciar timer
        if (currentMinutes === 0 && currentSeconds === 0) return;

        setIsRunning(true);
        const startData = { startedAt: new Date().toISOString() };
        sendTimerEvent("timer", currentMinutes, currentSeconds, true, startData);
        saveTimerToStorage(currentMinutes, currentSeconds, true, startData);
      }
    }
  };

  const handleStop = () => {
    setIsRunning(false);
    setMinutes('00');
    setSeconds('00');
    sendClearTimer();
    resetTimerLocal();
  };

  return (
    <div className={isFacilitatorUser ? styles.timerContainer : `${styles.timerContainer} ${styles.noControls}`} style={{position: 'relative'}}>
      {isFinished && <Confetti />}
      <div className={styles.timerTimeGroup}>
        {editing === 'minutes' ? (
          <input
            ref={minRef}
            className={styles.timeInput}
            value={minutes}
            onChange={handleMinutesChange}
            onBlur={handleBlur}
            onKeyDown={handleMinutesKeyDown}
            maxLength={2}
            style={{ width: 36, textAlign: 'center' }}
            disabled={isRunning || !isEditable}
          />
        ) : (
          <span
            className={styles.timeDisplay + (isFinished ? ' ' + styles.blink : '')}
            style={{ width: 36, textAlign: 'center', cursor: isRunning || !isEditable ? 'not-allowed' : 'pointer' }}
            onClick={() => isEditable && handleDisplayClick('minutes')}
            tabIndex={0}
          >
            {pad(minutes === '' ? '0' : minutes)}
          </span>
        )}
        <span className={styles.timeSeparator}>:</span>
        {editing === 'seconds' ? (
          <input
            ref={secRef}
            className={styles.timeInput}
            value={seconds}
            onChange={handleSecondsChange}
            onBlur={handleBlur}
            onKeyDown={handleSecondsKeyDown}
            maxLength={2}
            style={{ width: 36, textAlign: 'center' }}
            disabled={isRunning || !isEditable}
          />
        ) : (
          <span
            className={styles.timeDisplay + (isFinished ? ' ' + styles.blink : '')}
            style={{ width: 36, textAlign: 'center', cursor: isRunning || !isEditable ? 'not-allowed' : 'pointer' }}
            onClick={() => isEditable && handleDisplayClick('seconds')}
            tabIndex={0}
          >
            {pad(seconds === '' ? '0' : seconds)}
          </span>
        )}
      </div>
      {isFacilitatorUser && (
        <>
          <button className={styles.iconButton} aria-label={isRunning ? 'Pausar' : 'Iniciar'} onClick={handlePlayPause}>
            {isRunning ? (
              <svg width="60" height="60" viewBox="0 0 60 60">
                <rect x="20" y="18" width="5" height="24" rx="2.5" fill="white" stroke="white" strokeWidth="3"/>
                <rect x="35" y="18" width="5" height="24" rx="2.5" fill="white" stroke="white" strokeWidth="3"/>
              </svg>
            ) : (
              <svg width="60" height="60" viewBox="0 0 60 60">
                <polygon points="24,18 24,42 44,30" fill="white" stroke="white" strokeWidth="3"/>
              </svg>
            )}
          </button>
          <button className={styles.iconButton} aria-label="Parar" onClick={handleStop}>
            <svg width="60" height="60" viewBox="0 0 60 60">
              <rect x="20" y="20" width="20" height="20" rx="2" fill="none" stroke="white" strokeWidth="3"/>
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

export default Timer;
